import { FC, memo, useMemo, useState } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { useProps } from "contexts/PropsContext"

import { useStatsOverTime } from "hooks/useStatsOverTime"

import { formatFiatCurrency } from "util/formatter"

import { StatsAreaChart } from "./StatsAreaChart"
import { cumulativeEligible, currencyStats, statsOptions } from "./statsConfig"
import { StatsSelector } from "./StatsSelector"

interface StatisticChartProps {
  timeframe: (Date | null)[]
  tenant: string
}

const StatisticChart: FC<StatisticChartProps> = ({ timeframe, tenant }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [selectedStats, setSelectedStats] = useState<string[]>(["userCount"])
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [isCumulative, setIsCumulative] = useState(false)
  const [warning, setWarning] = useState("")
  const { darkMode } = useProps()

  const { loading, data: statsOverTime, granularity } = useStatsOverTime(timeframe, tenant, selectedStats)

  const formattedData = useMemo(
    () =>
      statsOverTime?.map((item: any) => ({
        ...item,
        ggr: item.wager - item.win
      })),
    [statsOverTime]
  )

  const handleStatToggle = (value: string, checked: boolean) => {
    setSelectedStats((prev) => {
      const clickedType = statsOptions.find((opt) => opt.value === value)?.type || "count"
      const selectedTypes = prev.map((val) => statsOptions.find((opt) => opt.value === val)?.type || "count")

      const isMixingTypes = selectedTypes.length > 0 && selectedTypes.some((t) => t !== clickedType)
      if (isMixingTypes && checked) {
        setWarning(t("charts.cannotMixStats"))
        setTimeout(() => setWarning(""), 10000)
        return prev
      }

      setIsCumulative(false)
      setWarning("")
      return checked ? [...prev, value] : prev.filter((v) => v !== value)
    })
  }

  const handleRemoveStat = (value: string) => {
    setIsCumulative(false)
    setSelectedStats((prev) => prev.filter((v) => v !== value))
  }

  const yAxisWidth = useMemo(() => {
    const hasData = Boolean(formattedData?.length) && selectedStats.length > 0
    const isCurrency = selectedStats.some((key) => statsOptions.find((s) => s.value === key)?.type === "currency")
    const fallbackSample = isCurrency ? "$0.00" : "0"

    if (!hasData) return Math.min(Math.max(fallbackSample.length * 8, 60), 140)

    const format = (v: number) => (isCurrency ? formatFiatCurrency(v) : String(v))

    let longest = ""
    for (const statKey of selectedStats) {
      const nums = formattedData!.map((d) => Number(d[statKey as keyof typeof d])).filter((v) => Number.isFinite(v))
      if (!nums.length) continue

      const max = Math.max(...nums)
      const min = Math.min(...nums)
      for (const s of [format(max), format(min)]) if (s.length > longest.length) longest = s
    }

    if (!longest) longest = fallbackSample

    return Math.min(Math.max(longest.length * 18, 60), 140)
  }, [formattedData, selectedStats])

  const spinnerColor = useMemo(() => {
    if (!selectedStats.length) return "#e5e7eb"
    const stat = statsOptions.find((s) => s.value === selectedStats[0])
    return stat?.color ?? "#e5e7eb"
  }, [selectedStats])

  const chartData = useMemo(() => {
    if (!formattedData?.length) return formattedData

    const sorted = [...formattedData].sort((a: any, b: any) => {
      const ta = a.periodTs ?? new Date(a.period).getTime()
      const tb = b.periodTs ?? new Date(b.period).getTime()
      return ta - tb
    })

    if (!isCumulative || selectedStats.length !== 1) return sorted

    const key = selectedStats[0]
    if (!cumulativeEligible.has(key)) return sorted

    let sum = 0
    return sorted.map((row: any) => {
      const next = { ...row }
      const v = Number(next[key])
      sum += Number.isFinite(v) ? v : 0
      next[key] = sum
      return next
    })
  }, [formattedData, selectedStats, isCumulative])

  const cumulativeDisabled = selectedStats.length !== 1 || !cumulativeEligible.has(selectedStats[0])

  return (
    <div className="flex flex-col gap-1 rounded w-full border border-[rgba(0,0,0,0.05)] text-black backdrop-blur-[9px] shadow-[0px_3px_6px_0px_rgba(192,192,192,0.6)] dark:border-[rgba(255,255,255,0.1)] dark:text-white">
      <div className="flex gap-4 items-center bg-[rgba(255,255,255,0.04)] py-6 px-8">
        <StatsSelector
          dropdownOpen={dropdownOpen}
          setDropdownOpen={setDropdownOpen}
          selectedStats={selectedStats}
          options={statsOptions}
          onToggleStat={handleStatToggle}
          onRemoveStat={handleRemoveStat}
        />

        <button
          type="button"
          onClick={() => setIsCumulative((v) => !v)}
          disabled={cumulativeDisabled}
          className={clsx(
            "px-4 py-2 rounded-full text-sm font-medium transition-colors border",
            cumulativeDisabled && "opacity-40 cursor-not-allowed pointer-events-none",
            !cumulativeDisabled &&
              (isCumulative
                ? "bg-black text-white border-black dark:bg-white dark:text-black dark:border-white"
                : "bg-transparent text-black border-zinc-300 hover:bg-zinc-100 dark:text-white dark:border-[rgba(255,255,255,0.2)] dark:hover:bg-white/10")
          )}
          title={cumulativeDisabled ? "Select exactly one cumulative-eligible stat" : "Toggle cumulative view"}
        >
          {t("charts.cumulative")}
        </button>

        {warning && <p className="mt-2 text-sm text-red">{warning}</p>}
      </div>

      <div className="text-xl font-normal text-center uppercase opacity-70 mt-7">{t("charts.statisticChart")}</div>

      <StatsAreaChart
        darkMode={darkMode}
        loading={loading}
        spinnerColor={spinnerColor}
        data={chartData}
        granularity={granularity}
        yAxisWidth={yAxisWidth}
        selectedStats={selectedStats}
        options={statsOptions}
        currencyStats={currencyStats}
      />
    </div>
  )
}

const MemoizedStatisticChart = memo(StatisticChart)
export { MemoizedStatisticChart as StatisticChart }
